import { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Skeleton,
  Alert,
  Typography,
  Paper,
  Stack,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Add as AddIcon } from '@mui/icons-material';
import { ROUTES } from '@/config/constants';
import { useYouTubeVideo } from '@/hooks/useYouTubeVideo';
import { useVideoSegments, useCreateYouTubeSegment, useDeleteYouTubeSegment, useUpdateYouTubeSegment } from '@/hooks/useYouTubeSegments';
import { useYouTubeCaptions } from '@/hooks/useYouTubeCaptions';
import { useYouTubeTranscriptEdit } from '@/hooks/useYouTubeTranscriptEdit';
import { useVideoPlayerStore } from '@/store/videoPlayerStore';
import { useProjectById } from '@/hooks/useProjects';
import { useYouTubeListeningTracker } from '@/hooks/useYouTubeListeningTracker';
import { useYouTubeVideoTranscripts } from '@/hooks/useYouTubeVideoTranscripts';
import { useOnlineListeningDrain } from '@/hooks/useListeningTime';
import { useCelebrations } from '@/hooks/useCelebrations';
import { useTrackSegmentPlay } from '@/hooks/useLearningMetrics';
import { ListeningMilestoneModal } from '@/components/celebrations/ListeningMilestoneModal';
import { YouTubePlayer } from '@/components/youtube/YouTubePlayer';
import type { YouTubePlayerRef } from '@/components/youtube/YouTubePlayer';
import { SegmentTimingForm } from '@/components/youtube/SegmentTimingForm';
import { VideoPlayerSegmentList } from '@/components/youtube/VideoPlayerSegmentList';
import { VideoPlayerControls } from './VideoPlayerControls';
import type { VideoPlayerControlsRef } from './VideoPlayerControls';
import { VideoPlayerTranscriptPanel } from './VideoPlayerTranscriptPanel';
import { VideoPlayerTranslationPanel } from './VideoPlayerTranslationPanel';
import { VideoPlayerVocabularyPanel } from './VideoPlayerVocabularyPanel';
import { VideoPlayerStudyTabs } from './VideoPlayerStudyTabs';
import type { StudyTab } from './VideoPlayerStudyTabs';
import { useYouTubeTranslations } from '@/hooks/useYouTubeTranslations';
import { useYouTubeTranslationEdit } from '@/hooks/useYouTubeTranslationEdit';
import { useVocabularyBySegment } from '@/hooks/useVocabulary';
import { TranscriptDownloadModal } from '@/components/common/TranscriptDownloadModal';
import { ListeningTimeWidget } from '@/components/common/ListeningTimeWidget';
import type { YouTubeSegment } from '@/types/youtubeSegments.types';

/**
 * VideoPlayer renders the study-mode YouTube player page.
 *
 * Route: /projects/:projectId/videos/:videoId
 *
 * Layout (mobile-first, additive):
 *   1. YouTubePlayer — full-width 16:9 video
 *   2. Title + Add Segment action
 *   3. SegmentTimingForm (when creating a new segment)
 *   4. VideoPlayerSegmentList — horizontal scrollable tablist strip
 *   5. VideoPlayerControls — Play/Pause + Loop toggle
 *   6. VideoPlayerTranscriptPanel — transcript shell with Get Transcript affordance (Story 2.4)
 *
 * Story 2.2: segment creation form + segment strip.
 * Story 2.3: loop, navigation, Play/Pause controls, segment context surface (AC 5 / Task 1.4).
 * Story 2.4: replace temp segment-context surface with durable VideoPlayerTranscriptPanel.
 */
export const VideoPlayer = () => {
  const { t } = useTranslation('player');
  const { projectId, videoId } = useParams<{ projectId: string; videoId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Ref to the YouTubePlayer — seekTo, play, pause (Story 2.3: extended ref).
  const playerRef = useRef<YouTubePlayerRef>(null);
  // Ref to VideoPlayerControls — focusPlay() for Task 4.3 focus handoff.
  const controlsRef = useRef<VideoPlayerControlsRef>(null);
  // Tracks whether the current play() call was initiated programmatically (chip or Controls Play).
  // When the YouTube native play button fires onStateChange('playing') and this is false,
  // that means the user pressed the native button — deselect the active segment.
  const isProgrammaticPlayRef = useRef(false);

  // Segment creation mode: false = idle, true = form visible.
  const [isAddingSegment, setIsAddingSegment] = useState(false);
  // Segment edit mode: null = idle, YouTubeSegment = form visible pre-populated.
  const [editingSegment, setEditingSegment] = useState<YouTubeSegment | null>(null);
  // Surfaces body-level errors from 2xx responses that carry status !== 'success'.
  const [segmentSaveBodyError, setSegmentSaveBodyError] = useState(false);
  // Segment pending deletion — null = dialog closed.
  const [segmentToDelete, setSegmentToDelete] = useState<YouTubeSegment | null>(null);
  // Interval enforcing the preview stop boundary — cleared on re-preview and unmount.
  const previewIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    return () => {
      if (previewIntervalRef.current) clearInterval(previewIntervalRef.current);
    };
  }, []);
  // Practice-reading download modal open state.
  const [downloadOpen, setDownloadOpen] = useState(false);

  // Player store — client/UI state only (no server data duplicated here).
  const currentTime = useVideoPlayerStore((s) => s.currentTime);
  const playerState = useVideoPlayerStore((s) => s.playerState);
  const activeSegmentId = useVideoPlayerStore((s) => s.activeSegmentId);
  const isLooping = useVideoPlayerStore((s) => s.isLooping);
  const lastWorkedSegmentId = useVideoPlayerStore((s) => s.lastWorkedSegmentId);
  const setActiveSegmentId = useVideoPlayerStore((s) => s.setActiveSegmentId);
  const setIsLooping = useVideoPlayerStore((s) => s.setIsLooping);
  const setLastWorkedSegmentId = useVideoPlayerStore((s) => s.setLastWorkedSegmentId);

  // Server data hooks.
  const { data: video, isLoading, isError } = useYouTubeVideo(videoId ?? '');
  const { data: segments = [], isLoading: segmentsLoading } = useVideoSegments(videoId ?? '');
  const createSegment = useCreateYouTubeSegment();
  const deleteSegment = useDeleteYouTubeSegment(videoId ?? '');
  const updateSegment = useUpdateYouTubeSegment(videoId ?? '');
  // Play-count tracking — fire-and-forget; failures are silently ignored (AudioPlayer pattern).
  const { mutate: trackSegmentPlay } = useTrackSegmentPlay();
  // Practice-reading download: loads user-created transcripts for the whole video.
  // Used both to show/hide the download button and to feed the modal (single cached query).
  const { data: downloadTranscripts = [], isPending: downloadLoading, isError: downloadError } = useYouTubeVideoTranscripts(videoId ?? '');

  useEffect(() => {
    if (downloadError) toast.error('Failed to load transcripts');
  }, [downloadError]);

  // Project data — needed to get projectLanguage for listening time tracking.
  const { data: project } = useProjectById(parseInt(projectId ?? '0', 10));

  // Celebrations — milestone modal shown when a listening time threshold is crossed.
  const { activeCelebration, checkAndMaybeSetMilestone, dismissCelebration } = useCelebrations();

  // Listening time tracker — reads playerState from store; enabled only once language is known.
  // AudioPlayer pattern: records only when playerState === 'playing', 30s accumulator.
  useYouTubeListeningTracker({
    language: project?.projectLanguage,
    enabled: !!project?.projectLanguage,
    onMilestoneCheck: checkAndMaybeSetMilestone,
  });

  // Drain any localStorage-buffered listening time from previous offline periods.
  useOnlineListeningDrain();

  // Tracks whether the underlying YT.Player has fired onReady — imperative ref calls
  // (seekTo/play/pause) are only guaranteed to reach a live player once this is true.
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const handlePlayerReady = useCallback(() => setIsPlayerReady(true), []);

  // Reset readiness when the video actually changes — YouTubePlayer destroys and
  // recreates its YT.Player on a videoId change and fires onReady again, so
  // isPlayerReady must not remain stale-true across the swap (there's a window where
  // the old player is gone but onReady for the new one hasn't fired yet). Guarded by
  // a ref so this only fires on a genuine change, not on the initial mount (where
  // onReady may fire in the same effect pass before/after this one, depending on
  // child/parent effect ordering).
  const prevYoutubeVideoIdRef = useRef<string | null>(null);
  useEffect(() => {
    const nextId = video?.youtube_video_id ?? null;
    if (prevYoutubeVideoIdRef.current !== null && prevYoutubeVideoIdRef.current !== nextId) {
      setIsPlayerReady(false);
    }
    prevYoutubeVideoIdRef.current = nextId;
  }, [video?.youtube_video_id]);

  // Deep link (?segmentId=N) from the Reports page: once segments load AND the player
  // is ready, select and seek to the linked segment. Applied at most once per mount;
  // unknown ids ignored.
  //
  // Readiness gating matters because React Query can return cached segments
  // synchronously (segments.length > 0 on first render) while the YouTube IFrame API
  // script/player is still loading — seeking against a not-yet-ready player is a no-op,
  // so the ref guard must not be marked "applied" until the seek can actually happen.
  const deepLinkAppliedRef = useRef(false);
  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    const param = searchParams.get('segmentId');
    if (!param) {
      deepLinkAppliedRef.current = true;
      return;
    }
    if (segments.length === 0) return; // wait for segments to load
    if (!isPlayerReady) return; // wait for the player to be ready to seek
    deepLinkAppliedRef.current = true;
    const target = segments.find((seg) => String(seg.id) === param);
    if (target) {
      isProgrammaticPlayRef.current = true;
      playerRef.current?.seekTo(target.start_time);
      playerRef.current?.pause();
      setActiveSegmentId(String(target.id));
    }
  }, [segments, searchParams, setActiveSegmentId, isPlayerReady]);

  // Reset store on unmount so playerState returns to 'idle' if user navigates mid-play.
  // This naturally stops the listening time tracker and prevents stale state on re-entry.
  useEffect(() => {
    return () => {
      useVideoPlayerStore.getState().reset();
    };
  }, []);

  // Track last-visited YouTube video so the Dashboard "Continue Learning" widget can surface it.
  useEffect(() => {
    if (!video || !videoId || !projectId) return;
    try {
      localStorage.setItem('scribelate-last-yt-video', JSON.stringify({
        videoId,
        projectId: parseInt(projectId, 10),
        title: video.title,
        thumbnailUrl: video.thumbnail_url ?? null,
        channelName: video.channel?.name ?? null,
        youtubeVideoId: video.youtube_video_id,
        timestamp: Date.now(),
      }));
    } catch {
      // localStorage unavailable — ignore silently.
    }
  }, [video, videoId, projectId]);

  // Caption fetch hook — fetchFromCache auto-fires on segment change (DB-only, fast).
  // fetchCaption is called explicitly by the "Get Transcript" button (triggers yt-dlp on miss).
  const { state: captionState, fetchCaption, fetchFromCache, prefetch: prefetchCaption, reset: resetCaption, evictFromCache } = useYouTubeCaptions();

  // Edit hook — manages textarea state and 5-second auto-save for user transcript edits.
  // Created here (owner level) and passed as a prop to the panel to avoid prop-drilling
  // through multiple layers while keeping the panel stateless.
  // onSaveSuccess evicts the warm prefetch cache so returning to this segment fetches
  // fresh DB text rather than the pre-save stale entry.
  const editHook = useYouTubeTranscriptEdit((segmentId) => evictFromCache(segmentId));

  // Translation fetch hook — parallel to caption hook; strictly user-initiated.
  const { state: translationState, fetchTranslation, fetchFromCache: fetchTranslationFromCache, prefetch: prefetchTranslation, reset: resetTranslation, evictFromCache: evictTranslationFromCache } = useYouTubeTranslations();

  // Translation edit hook — manages textarea state and 5-second auto-save for user translation edits.
  const translationEditHook = useYouTubeTranslationEdit((segmentId) => evictTranslationFromCache(segmentId));

  // Active study tab: 'transcript' or 'translation'.
  const [activeStudyTab, setActiveStudyTab] = useState<StudyTab>('transcript');

  // On segment change: auto-fetch from DB cache only (fast, no external APIs).
  // Cache hit → shows saved transcript/translation immediately. Cache miss → stays idle.
  // Save any in-progress edit before exiting — covers the loop-toggle path where
  // setActiveSegmentId fires and this effect runs while text is still unsaved.
  useEffect(() => {
    if (editHook.hasUnsavedChanges) void editHook.saveNow();
    editHook.exitEditMode();
    if (translationEditHook.hasUnsavedChanges) void translationEditHook.saveNow();
    translationEditHook.exitEditMode();
    if (activeSegmentId) {
      fetchFromCache(Number(activeSegmentId));
      fetchTranslationFromCache(Number(activeSegmentId));
    } else {
      resetCaption();
      resetTranslation();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSegmentId]);

  // Derive the currently selected segment object for loop-boundary seeking (Story 2.3, Task 2.2).
  const selectedSegment = useMemo(
    () => segments.find((seg) => String(seg.id) === activeSegmentId) ?? null,
    [segments, activeSegmentId],
  );

  // Story 2.6 — Proximity chip: segment whose time range contains currentTime, only
  // surfaced when no segment is explicitly selected (activeSegmentId is null).
  const proximitySegmentId = useMemo((): string | null => {
    if (activeSegmentId !== null) return null;
    const inRange = segments.find(
      (seg) => seg.start_time <= currentTime && currentTime < seg.end_time,
    );
    return inRange ? String(inRange.id) : null;
  }, [segments, currentTime, activeSegmentId]);

  // Proximity segment object — populated during free playback when the playhead is
  // inside a segment's time range (mirrors podcast player's handleProximitySegmentChange).
  const proximitySegment = useMemo(
    () =>
      proximitySegmentId
        ? (segments.find((s) => String(s.id) === proximitySegmentId) ?? null)
        : null,
    [segments, proximitySegmentId],
  );

  // Study panel display target: explicit chip selection takes priority over proximity.
  // During free playback this ensures the transcript panel follows the playhead automatically.
  const displaySegment = selectedSegment ?? proximitySegment;

  // panelSegmentRef tracks the last non-null displaySegment purely for gap detection.
  // Must be declared before isGap so the ref exists when isGap reads .current.
  const panelSegmentRef = useRef<typeof displaySegment>(null);
  if (displaySegment !== null) {
    panelSegmentRef.current = displaySegment;
  }

  // isGap: true while playback is running but the playhead is between segments.
  // Panels render an animated skeleton placeholder in this state instead of old stale content.
  const isGap = displaySegment === null && panelSegmentRef.current !== null;

  // Story 4.1: vocab words for the currently displayed segment.
  const vocabBySegment = useVocabularyBySegment(displaySegment?.id ?? 0);

  // 1-based display index for the study panel header.
  const segmentIndex = useMemo(() => {
    if (!displaySegment) return null;
    const idx = segments.findIndex((s) => s.id === displaySegment.id);
    return idx >= 0 ? idx + 1 : null;
  }, [segments, displaySegment]);

  // Auto-fetch cached transcript/translation when the playhead enters a segment during free
  // playback, and eagerly pre-fetch adjacent segments so the warm cache eliminates loading
  // flashes when the playhead crosses into the next or previous segment.
  useEffect(() => {
    if (activeSegmentId !== null) return; // explicit-selection effect owns this case
    // Exit any (auto-entered) edit mode before switching segments so the panels'
    // auto-edit effects re-key to the new proximity segment. If there are unsaved
    // changes, save first (fire-and-forget) to flush the debounce rather than
    // silently discarding the edit. exitEditMode cancels the pending debounce
    // timer so no duplicate save fires afterwards.
    if (editHook.hasUnsavedChanges) void editHook.saveNow();
    editHook.exitEditMode();
    if (translationEditHook.hasUnsavedChanges) void translationEditHook.saveNow();
    translationEditHook.exitEditMode();
    if (proximitySegmentId) {
      fetchFromCache(Number(proximitySegmentId));
      fetchTranslationFromCache(Number(proximitySegmentId));
      // Pre-fetch adjacent segments into the warm cache so the next fetchFromCache
      // call resolves synchronously (no loading state displayed).
      const idx = segments.findIndex((s) => String(s.id) === proximitySegmentId);
      if (idx >= 0) {
        if (idx + 1 < segments.length) {
          prefetchCaption?.(segments[idx + 1].id);
          prefetchTranslation?.(segments[idx + 1].id);
        }
        if (idx - 1 >= 0) {
          prefetchCaption?.(segments[idx - 1].id);
          prefetchTranslation?.(segments[idx - 1].id);
        }
      }
    }
    // No reset on gap exit — captionState/translationState are cleared by the
    // next fetchFromCache call when the playhead enters the following segment.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proximitySegmentId, activeSegmentId]);

  // Story 2.6 — lastWorkedSegmentId restore from localStorage on mount.
  // Key is per-video so the resume state is scoped to this video.
  useEffect(() => {
    if (!videoId) return;
    try {
      const stored = localStorage.getItem(`scribelate-last-worked-segment-${videoId}`);
      if (stored) setLastWorkedSegmentId(stored);
    } catch {
      // localStorage unavailable — ignore silently.
    }
  // Only run on mount (videoId is stable for the lifetime of this page).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  // Story 2.6 — persist lastWorkedSegmentId whenever it changes.
  useEffect(() => {
    if (!videoId || !lastWorkedSegmentId) return;
    try {
      localStorage.setItem(`scribelate-last-worked-segment-${videoId}`, lastWorkedSegmentId);
    } catch {
      // localStorage unavailable — ignore silently.
    }
  }, [videoId, lastWorkedSegmentId]);

  // Story 2.6 — set lastWorkedSegmentId when a transcript fetch reaches a terminal state.
  // 'success': transcript was fetched and saved.
  // 'unavailable': no captions exist for this video — user still engaged with the panel.
  // This fires whenever the caption status changes to one of these values while a segment
  // is active, ensuring the Resume chip accurately represents the last studied segment.
  useEffect(() => {
    if (
      (captionState.status === 'success' || captionState.status === 'no-captions') &&
      activeSegmentId
    ) {
      setLastWorkedSegmentId(activeSegmentId);
    }
  }, [captionState.status, activeSegmentId, setLastWorkedSegmentId]);

  // Story 2.6 — resumeSegment: the segment object that Resume button will jump to.
  // Derived from lastWorkedSegmentId so it always reflects the latest segment or null.
  const resumeSegment = useMemo(
    () =>
      lastWorkedSegmentId
        ? (segments.find((s) => String(s.id) === lastWorkedSegmentId) ?? null)
        : null,
    [lastWorkedSegmentId, segments],
  );

  // Guard against route drift: verify the fetched video belongs to this project.
  const projectIdMismatch =
    !isLoading && !isError && video != null &&
    video.project_id !== parseInt(projectId ?? '0', 10);

  const handleBack = () => {
    navigate(ROUTES.PROJECT_DETAIL.replace(':id', projectId ?? ''));
  };

  const handleAddSegmentClick = useCallback(() => {
    setIsAddingSegment(true);
  }, []);

  const handleCancelSegment = useCallback(() => {
    setIsAddingSegment(false);
  }, []);

  const handleEditSegment = useCallback((segment: YouTubeSegment) => {
    setEditingSegment(segment);
    setIsAddingSegment(false);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingSegment(null);
  }, []);

  const handleUpdateSegment = useCallback(
    async (startTime: number, endTime: number) => {
      if (!editingSegment) return;
      try {
        await updateSegment.mutateAsync({
          segmentId: editingSegment.id,
          request: { start_time: startTime, end_time: endTime },
        });
        playerRef.current?.seekTo(startTime);
        setEditingSegment(null);
      } catch {
        // updateSegment.isError renders via the error Alert below the form
      }
    },
    [editingSegment, updateSegment],
  );

  const handlePreviewSegment = useCallback(
    (startTime: number, endTime: number) => {
      if (editingSegment) {
        trackSegmentPlay({ segmentId: editingSegment.id });
      }
      isProgrammaticPlayRef.current = true;
      playerRef.current?.seekTo(startTime);
      playerRef.current?.play();
      if (previewIntervalRef.current) clearInterval(previewIntervalRef.current);
      previewIntervalRef.current = setInterval(() => {
        // Read fresh time from the store — a closure-captured currentTime never
        // updates inside the interval, so the preview would never pause at end_time.
        if (useVideoPlayerStore.getState().currentTime >= endTime) {
          playerRef.current?.pause();
          if (previewIntervalRef.current) {
            clearInterval(previewIntervalRef.current);
            previewIntervalRef.current = null;
          }
        }
      }, 100);
    },
    [editingSegment, trackSegmentPlay],
  );

  const handleSaveSegment = useCallback(
    async (startTime: number, endTime: number) => {
      if (!videoId) return;
      setSegmentSaveBodyError(false);
      try {
        const result = await createSegment.mutateAsync({
          video_id: videoId,
          start_time: startTime,
          end_time: endTime,
        });
        if (result.status === 'success' && result.segment) {
          playerRef.current?.seekTo(result.segment.start_time);
          setActiveSegmentId(String(result.segment.id));
          setIsAddingSegment(false);
        } else {
          setSegmentSaveBodyError(true);
        }
      } catch {
        // HTTP errors set createSegment.isError=true; the Alert renders via that flag.
      }
    },
    [videoId, createSegment, setActiveSegmentId],
  );

  // Chip click = seek to segment start and mark it active. Does NOT auto-play.
  // pause() is called after seekTo because the YouTube IFrame API can fire a transient
  // PLAYING state during a seek (even on a paused video), which leaves the video playing.
  // Explicitly pausing after the seek ensures the video always stays stopped when the
  // user selects a segment — they must press Play to start from that point.
  const handleSegmentClick = useCallback(
    (segment: YouTubeSegment) => {
      isProgrammaticPlayRef.current = true;
      playerRef.current?.seekTo(segment.start_time);
      playerRef.current?.pause();
      setActiveSegmentId(String(segment.id));
    },
    [setActiveSegmentId],
  );

  // Delete segment request — stores the segment so the confirmation dialog opens.
  const handleDeleteSegmentRequest = useCallback((segment: YouTubeSegment) => {
    setSegmentToDelete(segment);
  }, []);

  // Delete segment confirm — stop looping/active if the segment being deleted is active,
  // then fire the mutation. Clear segmentToDelete after invocation.
  const handleDeleteSegmentConfirm = useCallback(async () => {
    if (!segmentToDelete) return;
    if (segmentToDelete && String(segmentToDelete.id) === activeSegmentId) {
      setIsLooping(false);
      setActiveSegmentId(null);
    }
    const id = segmentToDelete.id;
    setSegmentToDelete(null);
    try {
      await deleteSegment.mutateAsync(id);
    } catch {
      // deleteSegment.isError will be true — inline Alert in dialog handles it.
    }
  }, [segmentToDelete, activeSegmentId, setIsLooping, setActiveSegmentId, deleteSegment]);

  // Delete segment cancel — close dialog without any mutation.
  const handleDeleteSegmentCancel = useCallback(() => {
    setSegmentToDelete(null);
  }, []);

  // KB-6: Loop button is disabled when playhead is not inside any segment.
  const loopDisabled = activeSegmentId === null && proximitySegmentId === null;

  // Loop toggle (AC 1): flip isLooping; no toast, no animation.
  const handleLoopToggle = useCallback(() => {
    if (activeSegmentId !== null) {
      // Normal toggle — segment already pinned
      setIsLooping(!isLooping);
    } else if (proximitySegmentId !== null) {
      // Promote proximity segment to active and start looping
      setActiveSegmentId(proximitySegmentId);
      setIsLooping(true);
    }
    // If both null, button is disabled — this branch is unreachable
  }, [activeSegmentId, proximitySegmentId, isLooping, setIsLooping, setActiveSegmentId]);

  // VideoPlayerControls Play:
  //   • Proximity segment (no explicit selection) → pin it first, same as clicking the chip.
  //   • Paused near segment end (within 0.25s) → restart from start_time.
  //   • Paused mid-segment → resume from current position (no seek).
  //   • No segment at current time → play from current position.
  const handlePlay = useCallback(() => {
    // If no segment is explicitly pinned but the playhead is inside a segment (proximity),
    // select it now — same effect as clicking the segment chip then pressing play.
    const effectiveSegment =
      selectedSegment ??
      (proximitySegmentId ? segments.find((s) => String(s.id) === proximitySegmentId) ?? null : null);

    if (!selectedSegment && proximitySegmentId) {
      setActiveSegmentId(proximitySegmentId);
    }

    if (effectiveSegment && currentTime >= effectiveSegment.end_time - 0.25) {
      playerRef.current?.seekTo(effectiveSegment.start_time);
    }
    if (effectiveSegment) {
      trackSegmentPlay({ segmentId: effectiveSegment.id });
    }
    isProgrammaticPlayRef.current = true;
    playerRef.current?.play();
  }, [selectedSegment, proximitySegmentId, segments, currentTime, setActiveSegmentId, trackSegmentPlay]);

  const handlePause = useCallback(() => {
    playerRef.current?.pause();
  }, []);

  // Loop iteration tracking (product decision): each loop restart counts as a play,
  // matching AudioPlayer semantics — fire-and-forget, same pattern as handlePlay.
  const handleLoopIteration = useCallback(
    (segmentId: number) => {
      trackSegmentPlay({ segmentId });
    },
    [trackSegmentPlay],
  );

  const handleSkipBack = useCallback(() => {
    playerRef.current?.seekTo(Math.max(0, currentTime - 5));
  }, [currentTime]);

  const handleSkipForward = useCallback(() => {
    playerRef.current?.seekTo(currentTime + 5);
  }, [currentTime]);

  // Detects native YouTube play button: onStateChange fires 'playing' without a programmatic
  // flag set → user pressed the iframe play button → deselect the active segment so playback
  // continues freely past the segment boundary.
  //
  // Exception: when looping is active, the PLAYING event is fired by the loop boundary
  // seek-back (seekTo inside the polling interval), NOT by the user — do not deselect in
  // that case or the segment/transcript context would reset on every loop iteration.
  const handlePlayerStateChange = useCallback(
    (state: string) => {
      if (state === 'playing') {
        if (isProgrammaticPlayRef.current) {
          isProgrammaticPlayRef.current = false;
        } else if (!isLooping) {
          setActiveSegmentId(null);
        }
      }
    },
    [setActiveSegmentId, isLooping],
  );

  return (
    <Box
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        px: { xs: 1, sm: 2 },
        py: 2,
        // 'clip' prevents horizontal bleed (same visual as 'hidden') without creating a
        // scroll container — required so position:sticky on the video embed works correctly.
        overflowX: 'clip',
      }}
    >
      {/* Loading skeleton */}
      {isLoading && (
        <>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <IconButton
            onClick={handleBack}
            aria-label={t('backToProject')}
            size="small"
            sx={{ minHeight: 44, minWidth: 44 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="body2" color="text.secondary">
            <Skeleton width={160} />
          </Typography>
        </Stack>
        <Paper elevation={0} sx={{ bgcolor: 'background.paper', borderRadius: 1, overflow: 'hidden' }}>
          <Skeleton
            variant="rectangular"
            width="100%"
            sx={{ aspectRatio: '16/9', maxHeight: { md: '40vh' } }}
          />
          <Box sx={{ p: 1.5 }}>
            <Skeleton width="60%" height={24} />
          </Box>
        </Paper>
        </>
      )}

      {/* Error state */}
      {(isError || projectIdMismatch) && !isLoading && (
        <>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <IconButton
              onClick={handleBack}
              aria-label={t('backToProject')}
              size="small"
              sx={{ minHeight: 44, minWidth: 44 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="body2" color="text.secondary">
              {t('videoPlayer')}
            </Typography>
          </Stack>
          <Alert severity="error" sx={{ mt: 2 }}>
            {t('videoLoadFailed')}
          </Alert>
        </>
      )}

      {/* Player area — rendered only when we have a valid video for this route */}
      {!isLoading && !isError && !projectIdMismatch && video && (
        <>
          {/* 1. Video player — sticky so it stays in view while the transcript scrolls.
               Extracted from the Paper so its own overflow:hidden does not block sticky.
               Top border-radius matches the original card appearance. */}
          <Box
            data-testid="sticky-player-box"
            sx={{
              position: 'sticky',
              // Offset by the AppBar height so the video sticks just below the blue title bar.
              // MUI default Toolbar: 56px on xs, 64px on sm+.
              top: { xs: '56px', sm: '64px' },
              zIndex: 'appBar',
              // width:'100%' is required — without it the sticky element can exceed the
              // parent's padded width in some browsers and cause horizontal scroll.
              width: '100%',
              overflow: 'hidden',
              borderRadius: '4px 4px 0 0',
              bgcolor: 'background.paper',
            }}
          >
            {/* Back navigation — inside sticky so it stays visible while scrolling */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1, pt: 1, pb: 0.5, minWidth: 0 }}>
              <IconButton
                onClick={handleBack}
                aria-label={t('backToProject')}
                size="small"
                sx={{ minHeight: 44, minWidth: 44, flexShrink: 0 }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="subtitle1" component="h1" fontWeight="medium" noWrap sx={{ minWidth: 0, flex: 1 }}>
                {video.title}
              </Typography>
              <ListeningTimeWidget />
            </Stack>
            <YouTubePlayer
              ref={playerRef}
              youtubeVideoId={video.youtube_video_id}
              title={video.title}
              selectedSegment={selectedSegment}
              isLooping={isLooping}
              onStateChange={handlePlayerStateChange}
              onReady={handlePlayerReady}
              onLoopIteration={handleLoopIteration}
              endAction={
                !isAddingSegment && !editingSegment && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddSegmentClick}
                    sx={{ minHeight: 44, flexShrink: 0 }}
                    aria-label={t('addSegment')}
                  >
                    {t('addSegment')}
                  </Button>
                )
              }
            />
          </Box>

          {/* Remaining scrollable content — bottom-radius only so the card visually
               continues from the sticky video above. */}
          <Paper elevation={0} sx={{ borderRadius: '0 0 4px 4px', overflow: 'hidden' }}>
          {/* 3. Inline segment creation/edit form — never a modal */}
          {(isAddingSegment || editingSegment !== null) && (
            <SegmentTimingForm
              currentTime={currentTime}
              onSave={editingSegment ? handleUpdateSegment : handleSaveSegment}
              onCancel={editingSegment ? handleCancelEdit : handleCancelSegment}
              isSaving={editingSegment ? updateSegment.isPending : createSegment.isPending}
              initialStartTime={editingSegment?.start_time}
              initialEndTime={editingSegment?.end_time}
              onPreview={handlePreviewSegment}
            />
          )}

          {/* Segment save error feedback */}
          {(createSegment.isError || segmentSaveBodyError) && (
            <Box sx={{ px: 2, pb: 1 }}>
              <Alert severity="error" sx={{ py: 0.5 }}>
                {t('segmentSaveFailed')}
              </Alert>
            </Box>
          )}

          {/* 4. Segment strip — horizontal scrollable tablist */}
          <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
            <VideoPlayerSegmentList
              segments={segments}
              isLoading={segmentsLoading}
              activeSegmentId={activeSegmentId}
              onSegmentClick={handleSegmentClick}
              onDeleteSegment={handleDeleteSegmentRequest}
              onEditSegment={handleEditSegment}
              proximitySegmentId={proximitySegmentId}
              lastWorkedSegmentId={lastWorkedSegmentId}
              resumeSegment={resumeSegment}
              onDownloadTranscripts={downloadTranscripts.length > 0 ? () => setDownloadOpen(true) : undefined}
            />
          </Box>

          {/* 5. Study controls + Study panel — Transcript + Translation tabs.
               Controls are placed after the tab bar, mirroring the podcast player layout.
               Always rendered inline (no mobile Drawer) so transcript is always visible. */}
          <VideoPlayerStudyTabs
            activeTab={activeStudyTab}
            onTabChange={setActiveStudyTab}
          />

          <VideoPlayerControls
            ref={controlsRef}
            isPlaying={playerState === 'playing'}
            onPlay={handlePlay}
            onPause={handlePause}
            isLooping={isLooping}
            onLoopToggle={handleLoopToggle}
            onSkipBack={handleSkipBack}
            onSkipForward={handleSkipForward}
            loopDisabled={loopDisabled}
          />

          {activeStudyTab === 'transcript' && (
            <VideoPlayerTranscriptPanel
              segment={displaySegment}
              segmentIndex={segmentIndex}
              captionState={captionState}
              editHook={editHook}
              isGap={isGap}
              onSegmentWorked={
                displaySegment
                  ? () => {
                      setActiveSegmentId(String(displaySegment.id));
                      setLastWorkedSegmentId(String(displaySegment.id));
                    }
                  : undefined
              }
              onGetTranscript={
                displaySegment
                  ? () => fetchCaption(displaySegment.id)
                  : undefined
              }
              onRefreshTranscript={
                displaySegment
                  ? () => fetchFromCache(displaySegment.id, { force: true })
                  : undefined
              }
              onExitEditMode={() => { if (!isLooping) setActiveSegmentId(null); }}
              projectId={parseInt(projectId ?? '0', 10)}
              projectLanguage={project?.projectLanguage}
              userLanguage={project?.userLanguage}
              segmentVocabWords={vocabBySegment.data ?? []}
            />
          )}

          {/* Translation panel — Story 3.3 */}
          {activeStudyTab === 'translation' && (
            <VideoPlayerTranslationPanel
              segment={displaySegment}
              segmentIndex={segmentIndex}
              translationState={translationState}
              editHook={translationEditHook}
              isGap={isGap}
              onGetTranslation={
                displaySegment
                  ? () => fetchTranslation(displaySegment.id)
                  : undefined
              }
              onSwitchToTranscript={() => setActiveStudyTab('transcript')}
            />
          )}

          {/* AI Vocabulary Card Generation panel — Phase 3 */}
          {activeStudyTab === 'vocabulary' && (
            <VideoPlayerVocabularyPanel
              projectId={parseInt(projectId ?? '0', 10)}
              videoId={videoId ?? ''}
              projectLanguage={project?.projectLanguage ?? ''}
              userLanguage={project?.userLanguage ?? ''}
              hasTranscript={segments.length > 0}
              segments={segments}
              onSegmentClick={(id) => {
                setActiveSegmentId(String(id));
                setActiveStudyTab('transcript');
              }}
            />
          )}

          </Paper>
        </>
      )}

      {/* Delete segment confirmation dialog (Story 2.5) */}
      <Dialog
        open={segmentToDelete !== null}
        onClose={handleDeleteSegmentCancel}
        aria-labelledby="delete-segment-dialog-title"
        aria-describedby="delete-segment-dialog-description"
      >
        <DialogTitle id="delete-segment-dialog-title">{t('deleteSegmentTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-segment-dialog-description">
            {segmentToDelete != null &&
              t('deleteSegmentBody', {
                n: segments.findIndex((s) => s.id === segmentToDelete.id) + 1,
                start: segmentToDelete.start_time.toFixed(1),
                end: segmentToDelete.end_time.toFixed(1),
              })}
          </DialogContentText>
          {deleteSegment.isError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {t('deleteSegmentFailed')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleDeleteSegmentCancel}>
            {t('deleteSegmentCancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteSegmentConfirm}
            disabled={deleteSegment.isPending}
          >
            {t('deleteSegmentConfirmButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Practice-reading transcript download modal */}
      <TranscriptDownloadModal
        open={downloadOpen}
        onClose={() => setDownloadOpen(false)}
        title={video?.title ?? ''}
        segments={downloadTranscripts.map((s) => ({
          startTime: s.start_time,
          endTime: s.end_time,
          transcription: s.transcription,
        }))}
        isLoading={downloadLoading}
      />

      {activeCelebration && (
        <ListeningMilestoneModal
          milestone={activeCelebration}
          onDismiss={dismissCelebration}
        />
      )}
    </Box>
  );
};
